import { createVRMAnimationClip } from "@pixiv/three-vrm-animation";
import { Html } from "@react-three/drei";
import * as TWEEN from "@tweenjs/tween.js";
import { Buffer } from "buffer";
import { useAtom } from "jotai";
import React, { type FC, useCallback, useEffect, useRef, useState } from "react";
import type { Object3D } from "three"; // 必要なモジュールをインポート
import * as THREE from "three";
import type { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import type { IdleMotionStrategy } from "~/reception/motion/idleMotionStrategy";
import { MOTION_EXPRESSION_PREFIX } from "~/reception/motion/receptionBowMotion";
import {
  animationFilePathAtom,
  characterIndexAtom,
  characterListVersionAtom,
  currentVRMAtom,
  isAllLoadedAtom,
  isProcessingAtom,
  model3dLoadedAtom,
  modelPositionAtom,
  modelRotationAtom,
  motion3dLoadedAtom,
  vrmListAtom,
  vrmPathAtom,
} from "./Atom";
import { selectByName } from "./components/db/CharacterDBUtils";
import { emitter as aivisEmitter } from "./TTS/aivisspeech/VoiceSynthesis"; // AivisSpeechのEventEmitterをインポート
import { emitter as voicevoxEmitter } from "./TTS/voicevox/VoiceSynthesis"; // VOICEVOXのEventEmitterをインポート
import { loadMixamoAnimation } from "./vrm_utils/loadMixamoAnimation";
import {
  createCharacter,
  getAnimationFiles,
  getIdleMotion,
  getVRMFiles,
  getVRMName,
  getVRMURL,
} from "./vrm_utils/VRMUtils";
import vrmCache from "./vrm_utils/vrmCache";
import sharedGLTFLoader from "./vrm_utils/vrmLoader";

export type Position = {
  position: [number, number, number];
  time: number;
};

export type Rotation = {
  rotation: [number, number, number];
  time: number;
};

type AnimationFile = {
  name: string;
  url?: string;
  buffer?: { type: "Buffer"; data: number[] };
  extension?: string;
};

interface ModelProps {
  onViewed: () => void;
  forceReparse?: boolean;
  /**
   * アニメーションが終わったとき、ランダムでIdleモーションに切り替える確率（0.0〜1.0）。
   * 0: 絶対に切り替えない（常に現在のアニメーションをループ）
   * 1: 必ず切り替える（毎回ランダムにIdleファイルを選択）
   * デフォルト: 0.5
   */
  idleSwitchProbability?: number;
  /**
   * 受付（/reception）専用の待機モーション選択ストラテジー（任意）。
   *
   * 指定された場合、アニメーション終了時の待機モーション選択を
   * 既定の `name.includes("Idle")` ランダム選択ではなく、このストラテジーに委譲する。
   * 未指定（chat 経路など）の場合は従来どおりの挙動を維持する。
   *
   * stale closure を避けるため、内部では ref に保持して update ループから参照する。
   */
  idleMotionStrategy?: IdleMotionStrategy;
}

const Model: FC<ModelProps> = ({
  onViewed,
  forceReparse = true,
  idleSwitchProbability = 0.5,
  idleMotionStrategy,
}) => {
  const [isProcessing] = useAtom(isProcessingAtom);
  const modelURLRef = useRef<string>("");
  // プリロード済みVRM/gltfのキャッシュ
  const animationPathRef = useRef<string>("");
  const idlePathRef = useRef<string>("");
  const [isVRMLoaded, setIsVRMLoaded] = useState(false);
  const [isCharacterLoaded, setIsCharacterLoaded] = useState(false);
  const [isViewed, setIsViewed] = useState(false);
  const [, setCurrentVRM] = useAtom(currentVRMAtom);
  const isBlinking = useRef(false);
  const isAnimationLoaded = useRef<boolean>(false);
  const isAnimationLoading = useRef<boolean>(false);
  const [animationFilePath] = useAtom(animationFilePathAtom);
  const [vrmPath, setVRMPath] = useAtom(vrmPathAtom);
  const [position, setPosition] = useAtom(modelPositionAtom);
  const [rotation, setRotation] = useAtom(modelRotationAtom);
  const [vrmList, setVrmList] = useAtom(vrmListAtom);
  const [characterIndex, setCharacterIndex] = useAtom(characterIndexAtom);
  const [, setCharacterListVersion] = useAtom(characterListVersionAtom);
  const [isInitLoaded, setIsInitLoaded] = useState(false);
  const [, setIsAllLoaded] = useAtom(isAllLoadedAtom);
  const [, setModel3dLoaded] = useAtom(model3dLoadedAtom);
  const [, setMotion3dLoaded] = useAtom(motion3dLoadedAtom);
  const [progress, setProgress] = useState<number>(0);
  const [isFirstModelReady, setIsFirstModelReady] = useState(false);
  // モデルの座標
  const defaultPosition = [0, -1.5, -9];

  // モーション一覧
  const animationFiles = useRef<AnimationFile[]>([]);

  // 受付専用の待機モーション選択ストラテジー。
  // update ループ（deps []）はクロージャ固定のため、stale を避けて ref 経由で最新を読む。
  const idleMotionStrategyRef = useRef<IdleMotionStrategy | undefined>(idleMotionStrategy);
  idleMotionStrategyRef.current = idleMotionStrategy;

  // モデルが切り替わる時間
  const morphTime = 0.6;
  // モデルの口の形を変更するスピード
  const phonemeSpeed = 0.12;
  // 表情が切り替わるスピード
  const expressionSpeed = 0.07;
  const modelRef = useRef<Object3D | null>(null);
  const animationFrameId = useRef<number | null>(null);
  const loadRequestIdRef = useRef(0);
  const inFlightLoadByPathRef = useRef<Map<string, number>>(new Map());
  const hasMarkedModel3dLoadedRef = useRef(false);
  const hasStartedBackgroundPrefetchRef = useRef(false);

  // VRM要素の準備
  // eslint-disable-next-line -- fbxとvrmaどちらも扱うためany
  const currentVrm: any = useRef<any>(null);
  // eslint-disable-next-line -- fbxとvrmaどちらも扱うためany
  const currentVrmAnimation: any = useRef<any>(null);
  // eslint-disable-next-line -- fbxとvrmaどちらも扱うためany
  const currentMixer: any = useRef<any>(null);
  // eslint-disable-next-line -- アニメーションの処理を保存(Three.AnimationClip型) (fbxとvrmaどちらも扱うためany)
  const currentClip: any = useRef<any>(null);

  // 受付（strategy 注入時）専用: strategy が選んだ待機モーションを即再生する。
  // - strategy 未注入（/chat 経路）・animationFiles 未ロード・strategy が null 選択・
  //   URL 生成失敗のいずれかなら false を返し、呼び出し側の従来フォールバックへ委ねる。
  // - loadAnimation は isAnimationLoading.current=true だと no-op のため、再生前にリセットする
  //   （再生中クリップ途中の強制切替を許容し、Waving を早く止める）。
  const playStrategyIdleIfAvailable = (): boolean => {
    const strategy = idleMotionStrategyRef.current;
    if (!strategy || animationFiles.current.length === 0) return false;

    const choice = strategy.pickIdle(animationFiles.current);
    if (!choice) return false;

    const url = ensureAnimationFileUrl(choice);
    if (!url) {
      console.error("[playStrategyIdleIfAvailable] failed to create idle animation URL", choice);
      return false;
    }

    isAnimationLoading.current = false;
    loadAnimation(url, choice.extension);
    return true;
  };

  const playIdleAnimationIfReady = useCallback(() => {
    // 受付では strategy 選択を優先。未注入/選択不可時は従来どおり idlePathRef を再生（逐語不変）。
    if (playStrategyIdleIfAvailable()) return;

    if (!idlePathRef.current) {
      return;
    }

    isAnimationLoading.current = false;
    loadAnimation(idlePathRef.current);
  }, []);

  // (bootstrap moved below to reuse loadVRM and avoid duplicating "apply parsed" logic)

  // オーディオクリップの初期化
  const initAnimationClip = (clip: THREE.AnimationClip | null = null) => {
    if (currentVrm.current && currentVrmAnimation.current) {
      // const pastMixer=currentMixer.current.clipAction(currentClip.current);
      currentMixer.current = new THREE.AnimationMixer(currentVrm.current.scene);

      // 現在のアニメーションを停止して、新しいアニメーションを読み込む
      // クリップがあればそれを使う
      // currentMixer.current.clipAction(currentClip.current).fadeOut(morphTime);
      if (clip) {
        currentClip.current = clip;
        // currentMixer.current.clipAction(currentClip.current).fadeOut(morphTime);
      } else {
        // currentClip.current = createVRMAnimationClip(currentVrmAnimation.current, currentVrm.current);
        currentMixer.current.clipAction(currentClip.current).fadeOut(morphTime);
      }
      currentMixer.current.clipAction(currentClip.current).fadeIn(morphTime);
      // currentMixer.current.clipAction(currentClip.current).crossFadeFrom(pastMixer,morphTime);

      // アニメーションの切り替えデバッグする際30倍速
      //currentMixer.current.timeScale = 30;

      currentMixer.current.clipAction(currentClip.current).play();
      setIsViewed(true);
    }
    isAnimationLoading.current = false;
    // console.log("animationLoaded!", currentClip.current)
  };

  useEffect(() => {
    // Keep VRM pipeline constrained to avoid startup spikes.
    vrmCache.setConcurrency({ networkConcurrency: 3, parseConcurrency: 4 });
  }, []);

  useEffect(() => {
    // モデルが表示されたら親コンポーネントに通知
    if (isViewed) {
      onViewed();
    }
  }, [isViewed]);
  const loader: GLTFLoader = sharedGLTFLoader;

  const runOnIdle = (fn: () => void) => {
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    };
    if (typeof win.requestIdleCallback === "function") {
      try {
        win.requestIdleCallback(fn, { timeout: 200 });
      } catch {
        setTimeout(fn, 50);
      }
    } else {
      setTimeout(fn, 50);
    }
  };

  const ensureAnimationFileUrl = (file: {
    name: string;
    url?: string;
    buffer?: { type: "Buffer"; data: number[] };
    extension?: string;
  }) => {
    if (file.url && file.url !== "") return file.url;
    if (file.buffer && file.buffer.data && file.buffer.data.length > 0) {
      try {
        const blob = new Blob([Buffer.from(file.buffer.data)]);
        const url = URL.createObjectURL(blob) + (file.extension ?? "");
        file.url = url;
        return url;
      } catch (e) {
        console.error("Failed to create animation blob URL:", e);
        return "";
      }
    }
    return file.url ?? "";
  };

  const primeRawCacheFromBuffer = (
    url: string,
    buffer?: {
      type: "Buffer";
      data: number[];
    }
  ) => {
    if (!url || !buffer?.data || buffer.data.length === 0) return;
    if (vrmCache.hasRaw(url)) return;

    const rawBytes = Uint8Array.from(buffer.data);
    const now = Date.now();
    vrmCache.setRaw(url, {
      buffer: rawBytes.buffer,
      lastAccessed: now,
      size: rawBytes.byteLength,
    });
  };

  const markModel3dLoadedOnce = () => {
    if (hasMarkedModel3dLoadedRef.current) return;
    hasMarkedModel3dLoadedRef.current = true;
    setModel3dLoaded(true);
    setIsFirstModelReady(true);
  };

  const ensureCharacterRecord = async (
    modelPath: string,
    vrm: Parameters<typeof getVRMName>[0]
  ) => {
    // modelPathと対応するvrmListのパスを取得
    let globalName = "";
    for (const vrmData of vrmList) {
      if (vrmData.url === modelPath) {
        globalName = vrmData.name.replace(/\.vrm$/i, "");
        break;
      }
    }

    const name = getVRMName(vrm);
    const character = await selectByName(name);
    // DBにキャラクターがいない場合は新規作成
    if (character === null) {
      console.error("CharacterNotFound:キャラクターを新規作成します");
      await createCharacter(name, globalName, "VOICEVOX"); // デフォルトのためengineはVOICEVOX
      setCharacterListVersion((prev) => prev + 1);
    }
  };

  const loadVRM = async (
    modelPath: string,
    shouldSetCurrent: boolean = true,
    isCurrentRequest?: () => boolean
  ) => {
    if (modelPath === "") {
      console.error("モデルのパスがありませんでした");
      return;
    }

    try {
      setProgress(0);
      let parsed: Awaited<ReturnType<typeof vrmCache.parseRawWithLoader>>;
      if (forceReparse) {
        parsed = await vrmCache.parseRawWithLoader(
          modelPath,
          loader,
          (xhr) => {
            if (xhr?.total && xhr.total > 0) {
              const ratio = xhr.loaded / xhr.total;
              setProgress(Math.max(0, Math.min(100, Math.round(ratio * 100))));
            }
          },
          { cache: true }
        );
      } else {
        parsed = await vrmCache.loadOrGet(modelPath, loader, {
          onProgress: (value) => {
            setProgress(Math.max(0, Math.min(100, Math.round(value))));
          },
        });
      }
      if (!parsed) return;
      setProgress(100);

      const { vrm, gltf: tmpGltf } = parsed;

      if (isCurrentRequest && !isCurrentRequest()) return;

      // プリロード時（shouldSetCurrent=false）はcurrentVRM/currentVrm.current/gltfを更新しない
      if (shouldSetCurrent) {
        // 表情を笑顔にする
        currentVrm.current = vrm;
        // vrm.expressionManager.setValue('relux', 0.7);
        // vrm.expressionManager.setValue('oh', 1.0);

        // 視線をカメラの方に向ける
        const lookAtTarget = new THREE.Object3D();
        lookAtTarget.position.set(0, 0, 1);
        if (vrm.lookAt) {
          vrm.lookAt.autoUpdate = true;
          vrm.lookAt.target = lookAtTarget;
        }

        isBlinking.current = true; // randomBlink

        setCurrentVRM(vrm);
        setGltf(tmpGltf);
        markModel3dLoadedOnce();
      }

      // Fire-and-forget: don't block rendering on DB ops
      void Promise.resolve()
        .then(() => ensureCharacterRecord(modelPath, vrm))
        .catch((err) => console.error("ensureCharacterRecord failed:", err));
    } catch (error) {
      console.error("loadVRM failed:", error);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      let modelURL = "";
      try {
        if (vrmPath === "") {
          const vrmData = await getVRMFiles();
          if (!vrmData || vrmData.length === 0) {
            console.info(
              "No VRM files available in cache and network disabled; skipping VRM setup."
            );
          } else {
            // Resolve URLs
            for (const vrm of vrmData) {
              vrm.url = getVRMURL(vrmData, vrm.name);
            }

            // Prime only the first model's raw cache synchronously to speed up first render
            const firstVrm = vrmData[0];
            if (firstVrm) {
              primeRawCacheFromBuffer(firstVrm.url, firstVrm.buffer);
            }

            // Schedule background priming for the rest to run on idle
            runOnIdle(() => {
              try {
                const remaining = vrmData
                  .slice(1)
                  .map((v) => v.url)
                  .filter((u): u is string => Boolean(u));
                if (remaining.length > 0) {
                  void vrmCache
                    .prefetchRawAll(remaining, 3)
                    .catch((e) => console.error("background prefetch failed:", e));
                }
              } catch (e) {
                console.error("scheduling background prime failed:", e);
              }
            });

            modelURL = vrmData[0].url;
            setVrmList(vrmData as typeof vrmList);
          }
        } else {
          modelURL = vrmPath;
        }

        setVRMPath(modelURL);

        const idleBuffer = await getIdleMotion();

        if (idleBuffer) {
          const idleBlob = new Blob([Buffer.from(idleBuffer.data)]);
          const idlePath = URL.createObjectURL(idleBlob);
          idlePathRef.current = idlePath + ".fbx"; // fbxとvrmaで処理が異なるため拡張子を付与
          setIsVRMLoaded(true); // 参照を更新
        }
      } catch (error) {
        console.error("Error fetching idle motion buffer:", error);
      }
    };
    isAnimationLoading.current = false;
    if (!currentVrm.current) {
      // Fetch data (VRM files + idle) immediately; animation files deferred until first model ready
      void (async () => {
        if (!isAnimationLoaded.current) {
          // Defer animation fetching until after first model is ready to avoid startup contention
          isAnimationLoaded.current = true;
        }
      })();
      fetchData();
    } else {
      console.info("VRMロード済みなのでロードをスキップします");
    }
  }, []);

  // After first model ready, load animations lazily (do not block first render)
  useEffect(() => {
    if (!isFirstModelReady) return;
    if (animationFiles.current.length > 0) return; // already loaded

    (async () => {
      try {
        const files = await getAnimationFiles();

        if (!files || files.length === 0) {
          setMotion3dLoaded(true);
          return;
        }

        // Do not eagerly create blob URLs; store metadata and buffer and create URLs lazily when used
        const metaFiles: AnimationFile[] = files.map((file: AnimationFile) => ({
          name: file.name,
          buffer: file.buffer,
          extension: file.extension,
          url: file.url,
        }));

        animationFiles.current = metaFiles.map((f) => ({
          name: f.name,
          url: f.url ? f.url + (f.extension ?? "") : undefined,
          extension: f.extension,
          buffer: f.buffer,
        }));

        // Keep raw buffers attached in case lazy creation needed later (they stay in 'files')
        animationFiles.current.forEach((af, idx) => {
          af.buffer = files[idx].buffer;
        });

        // 受付（strategy 注入時）: animationFiles が揃った直後に待機モーションへ即切替する。
        // 起動時／ページマウント直後はサーバ由来の Waving.fbx（idlePathRef）が再生中のため、
        // これを最短で受付用の待機モーションへ置換する。strategy 未注入（/chat 経路）や
        // strategy が null 選択のときは何もしない（従来どおり現在クリップをループ）。
        playStrategyIdleIfAvailable();

        setMotion3dLoaded(true);
      } catch (error) {
        console.error("Deferred animation files fetch failed:", error);
      }
    })();
  }, [isFirstModelReady]);

  const [gltf, setGltf] = useState<GLTF>();
  // 現在の表情

  const phonemeValues = useRef<{ [key: string]: number }>({
    aa: 0.0,
    ee: 0.0,
    ih: 0.0,
    oh: 0.0,
    ou: 0.0,
  });
  const expressionValues = useRef<{ [key: string]: number }>({
    neutral: 0.0,
    happy: 0.0,
    angry: 0.0,
    sad: 0.0,
    surprised: 0.0,
  });

  useEffect(() => {
    if (!gltf) {
      const prepareInitialVRM = async () => {
        // 初期表示するVRMだけ決めて、実際のロードは vrmPath effect 側に一本化する
        const targetUrl =
          vrmPath !== "" ? vrmPath : vrmList.find((v) => /\.vrm$/i.test(v.name))?.url;
        if (targetUrl && vrmPath !== targetUrl) {
          setVRMPath(targetUrl);
        }
      };
      try {
        if (vrmList.length > 0) {
          // 初期準備のみ実施。モデルロードは vrmPath 変更時の effect でのみ行う。
          void prepareInitialVRM().catch((error) => {
            console.error("Error preparing initial VRM:", error);
          });
        }
      } catch (error) {
        console.error("Error fetching idle motion buffer:", error);
      }
    }
  }, [isVRMLoaded, characterIndex]);

  useEffect(() => {
    if (!isFirstModelReady) return;
    if (hasStartedBackgroundPrefetchRef.current) return;

    const currentModelPath = modelURLRef.current || vrmPath;
    const allUrls = vrmList.map((v) => v.url).filter((u): u is string => Boolean(u));
    const backgroundTargets = allUrls.filter((url) => url !== currentModelPath);

    if (backgroundTargets.length === 0) {
      hasStartedBackgroundPrefetchRef.current = true;
      return;
    }

    hasStartedBackgroundPrefetchRef.current = true;
    runOnIdle(() => {
      void vrmCache.prefetchRawAll(backgroundTargets, 3).catch((error) => {
        hasStartedBackgroundPrefetchRef.current = false;
        console.error("VRM background raw prefetch failed:", error);
      });
    });
  }, [isFirstModelReady, vrmList, vrmPath]);

  // アニメーション読み込み
  useEffect(() => {
    try {
      if (idlePathRef.current && currentVrm.current && isCharacterLoaded) {
        // strategy 注入時（受付）は strategy 選択を優先する。
        // この effect は animationFiles ロード完了 effect より後に発火し得るため、
        // ここで無条件に idlePathRef（=サーバ由来 Waving.fbx）を再生すると
        // 直前に切替えた受付待機モーションを上書きし Waving が一瞬再表示される。
        // playIdleAnimationIfReady に一本化して経路を揃える:
        // strategy 未注入（/chat）時は従来と逐語同等（idlePathRef を再生）。
        playIdleAnimationIfReady();
      }
    } catch (error) {
      console.error("Error fetching idle motion buffer:", error);
    }
  }, [idlePathRef.current, currentVrm.current, isCharacterLoaded]);

  // 発音に応じてVRMの口の形を変更する
  useEffect(() => {
    let targetPhoneme = "";
    let targetExpression = "";
    let isMounted = true; // Flag to track component mount status

    const handlePhoneme = (phoneme: string) => {
      // VRMに適用する発音を設定
      switch (phoneme) {
        case "a":
          targetPhoneme = "aa";
          break;
        case "e":
          targetPhoneme = "ee";
          break;
        case "i":
          targetPhoneme = "ih";
          break;
        case "o":
          targetPhoneme = "oh";
          break;
        case "u":
          targetPhoneme = "ou";
          break;
        // case 'N':
        // case 'n':
        // case 'end':
        // case 'pau':
        default:
          // 再生終了または無音時間のとき、元の口に戻す
          targetPhoneme = "";
          break;
      }
    };
    //animationFilesの中から指定の名前が含まれるファイルを再生する関数
    const playEmoMotion = (animationName: string) => {
      const emoPathes = animationFiles.current.filter((file) =>
        file.name.toLowerCase().includes(animationName.toLowerCase())
      );
      const choice = emoPathes[Math.floor(Math.random() * emoPathes.length)];
      // animationFilePath = emoPath;
      if (choice) {
        const url = ensureAnimationFileUrl(choice);
        if (!url) {
          console.error("[playEmoMotion] failed to create animation URL for choice", choice);
        } else {
          loadAnimation(url, choice.extension);
        }
      }
    };
    // 受付（reception）専用: ファイル名を完全一致で指定してモーションを再生する。
    // - playEmoMotion の部分一致 + ランダム選択とは異なり、name 完全一致のみを再生する。
    // - 一致が無い場合は fallback せず（strict）、利用可能名の一覧つきで一度だけ通知する。
    let hasInfoedNoExactMotion = false;
    const playExactMotion = (fileName: string) => {
      const choice = animationFiles.current.find((file) => file.name === fileName);
      if (!choice) {
        if (!hasInfoedNoExactMotion) {
          hasInfoedNoExactMotion = true;
          console.info(
            "[playExactMotion] 指定したファイル名が animationFiles に見つかりません。" +
              " ファイル名（拡張子含む完全一致）を確認してください。",
            { expected: fileName, available: animationFiles.current.map((f) => f.name) }
          );
        }
        return;
      }
      const url = ensureAnimationFileUrl(choice);
      if (!url) {
        console.error("[playExactMotion] failed to create animation URL for choice", choice);
        return;
      }
      loadAnimation(url, choice.extension);
    };
    const handleExpression = (expression: string) => {
      // 受付からの「ファイル名指定（完全一致）」モーション再生。
      // chat は "motion:" を emit しないため、この分岐は chat では不活性。
      if (expression.startsWith(MOTION_EXPRESSION_PREFIX)) {
        const fileName = expression.slice(MOTION_EXPRESSION_PREFIX.length);
        // 旧 case "bow" と同じ視覚的意味（表情はニュートラル、瞬きは有効）。
        targetExpression = "neutral";
        isBlinking.current = true;
        playExactMotion(fileName);
        return;
      }
      // VRMに適用する表情を設定
      switch (expression) {
        case "neutral":
          targetExpression = "neutral";
          isBlinking.current = true;
          break;
        case "happy":
          targetExpression = "happy";
          isBlinking.current = false;
          playEmoMotion(expression);
          break;
        case "angry":
          targetExpression = "angry";
          isBlinking.current = true;
          playEmoMotion(expression);
          break;
        case "sad":
          targetExpression = "sad";
          isBlinking.current = false;
          playEmoMotion(expression);
          break;
        case "surprised":
          targetExpression = "surprised";
          isBlinking.current = true;
          playEmoMotion(expression);
          break;
        case "bow":
          targetExpression = "neutral";
          isBlinking.current = true;
          playEmoMotion(expression);
          break;
        case "end":
        default:
          // 再生終了または無音時間のとき、元の口に戻す
          targetExpression = "neutral";
          isBlinking.current = true;
          break;
      }
    };

    const update = () => {
      if (!isMounted) return; // Check if component is still mounted before updating
      const maxPhonemeValue = 0.7;
      const minPhonemeValue = 0.0;
      const maxExpressionValue = 1.0;
      const minExpressionValue = 0.0;
      const closeFactor = 0.5;
      // 発音を毎フレーム0.1ずつ増減させる
      if (currentVrm.current && currentVrm.current.expressionManager) {
        for (const phoneme in phonemeValues.current) {
          if (phoneme === targetPhoneme) {
            phonemeValues.current[phoneme] = Math.min(
              maxPhonemeValue,
              phonemeValues.current[phoneme] + phonemeSpeed
            );
          } else {
            phonemeValues.current[phoneme] = Math.max(
              minPhonemeValue,
              phonemeValues.current[phoneme] - phonemeSpeed * closeFactor
            );
          }
          currentVrm.current.expressionManager.setValue(phoneme, phonemeValues.current[phoneme]);
        }

        // 表情を毎フレーム0.1ずつ増減させる
        for (const expression in expressionValues.current) {
          if (expression === targetExpression) {
            expressionValues.current[expression] = Math.min(
              maxExpressionValue,
              expressionValues.current[expression] + expressionSpeed
            );
          } else {
            expressionValues.current[expression] = Math.max(
              minExpressionValue,
              expressionValues.current[expression] - expressionSpeed * closeFactor
            );
          }

          currentVrm.current.expressionManager.setValue(
            expression,
            expressionValues.current[expression]
          );
        }
      }
      requestAnimationFrame(update);
    };
    // 瞬きを制御する
    let isBlink = false;
    const blink = () => {
      if (!isMounted) return; // Check if component is still mounted before updating
      if (isBlink) return;
      isBlink = true;
      const minBlinkInterval = 1500;
      const intervalRange = 2500;
      const randomDelay = Math.random() * intervalRange + minBlinkInterval; // 1.5秒から4秒の間でランダムな遅延を生成
      setTimeout(() => {
        const interval = 5;
        const steps = 30;
        let currentValue = 0;
        let direction = 1;

        const setValueWithEasing = () => {
          if (direction === -1) {
            currentValue += direction * (1 / steps);
          } else {
            currentValue += direction * 2 * (1 / steps);
          }
          currentVrm.current?.expressionManager.setValue("blink", currentValue);
          if (currentValue >= 1 || !isBlinking?.current) {
            direction = -1;
          }
          if (currentValue <= 0) {
            isBlink = false;
            blink();
            return;
          }
          setTimeout(setValueWithEasing, interval);
        };
        setValueWithEasing();
      }, randomDelay);
    };
    voicevoxEmitter.on("phoneme", handlePhoneme);
    voicevoxEmitter.on("expression", handleExpression);
    aivisEmitter.on("phoneme", handlePhoneme);
    aivisEmitter.on("expression", handleExpression);

    requestAnimationFrame(update);
    requestAnimationFrame(blink);

    return () => {
      isMounted = false;
      voicevoxEmitter.off("phoneme", handlePhoneme);
      voicevoxEmitter.off("expression", handleExpression);
      aivisEmitter.off("phoneme", handlePhoneme);
      aivisEmitter.off("expression", handleExpression);
    };
  }, []);

  //表情を元に戻すuseEffect
  useEffect(() => {
    if (!isProcessing) {
      voicevoxEmitter.emit("expression", "end"); // 表情を通知
      aivisEmitter.emit("expression", "end"); // 両方のエミッターに送信
    }
  }, [isProcessing]);

  const resetMouthShape = () => {
    if (currentVrm.current) {
      // Reset all mouth shapes to default
      currentVrm.current.expressionManager.setValue("aa", 0.0);
      currentVrm.current.expressionManager.setValue("ee", 0.0);
      currentVrm.current.expressionManager.setValue("ih", 0.0);
      currentVrm.current.expressionManager.setValue("oh", 0.0);
      currentVrm.current.expressionManager.setValue("ou", 0.0);
    }
  };
  // Reset mouth shape to default
  useEffect(() => {
    resetMouthShape();
  }, []);

  const clock = new THREE.Clock();
  clock.start();

  // ポーズのアップデート(常時ループしてる)
  useEffect(() => {
    const update = () => {
      const deltaTime = clock.getDelta();
      if (currentMixer.current) {
        try {
          if (
            !isAnimationLoading.current &&
            currentMixer.current.time > currentClip.current.duration - morphTime
          ) {
            //アニメーションは1秒以上必要
            //Idleモーションに切り替える確率（idleSwitchProbabilityで制御）
            if (animationFiles.current.length > 0 && Math.random() < idleSwitchProbability) {
              const strategy = idleMotionStrategyRef.current;
              if (strategy) {
                // 受付専用ストラテジーが注入されている場合は、選択をストラテジーに委譲する。
                // chat 経路（ストラテジー未注入）は下の else 側で従来どおり動作する。
                const choice = strategy.pickIdle(animationFiles.current);
                if (choice === null) {
                  // 該当モーションなし → 現在のアニメーションをループ
                  initAnimationClip(currentClip.current);
                } else {
                  const url = ensureAnimationFileUrl(choice);
                  if (!url) {
                    console.error(
                      "[update] failed to create idle animation URL for choice",
                      choice
                    );
                  } else {
                    loadAnimation(url, choice.extension);
                  }
                }
              } else {
                // animationFilesの中から、nameにIdleが含まれるファイルを全て取得
                // その中からランダムで1つ選びurlを取得
                const idlePathes = animationFiles.current.filter((file) =>
                  file.name.includes("Idle")
                );

                if (idlePathes.length > 0) {
                  const choice = idlePathes[Math.floor(Math.random() * idlePathes.length)];
                  const url = ensureAnimationFileUrl(choice);
                  if (!url) {
                    console.error(
                      "[update] failed to create idle animation URL for choice",
                      choice
                    );
                  } else {
                    loadAnimation(url, choice.extension);
                  }
                } else {
                  console.error("No Idle animations found, looping current animation");
                  initAnimationClip(currentClip.current);
                }
              }
            } else {
              // 読み込み済みのアニメーションをループ
              initAnimationClip(currentClip.current);
            }
          } else {
            // セットしたアニメーションを更新(ここで画面に反映)
            currentMixer.current.update(deltaTime);
            if (currentVrm.current) {
              currentVrm.current.update(deltaTime);
            }
          }
        } catch (e) {
          console.error("Error updating animation", e);
        }
      }
      animationFrameId.current = requestAnimationFrame(update);
    };
    update();
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, []);

  useEffect(() => {
    if (animationPathRef.current !== animationFilePath) {
      animationPathRef.current = animationFilePath;
      loadAnimation(animationFilePath);
    }
  }, [animationFilePath]);

  useEffect(() => {
    if (vrmPath === "") return;
    if (modelURLRef.current === vrmPath) return;

    const existingRequestForPath = inFlightLoadByPathRef.current.get(vrmPath);
    if (existingRequestForPath) {
      return;
    }

    const requestId = ++loadRequestIdRef.current;
    inFlightLoadByPathRef.current.set(vrmPath, requestId);
    const isCurrentRequest = () => loadRequestIdRef.current === requestId;

    const loadModel = async () => {
      await loadVRM(vrmPath, true, isCurrentRequest);
      if (!isCurrentRequest()) return;
      modelURLRef.current = vrmPath;
      setIsCharacterLoaded(true);
      setIsInitLoaded(true);
      isAnimationLoading.current = false;
      playIdleAnimationIfReady();
      // Warm idle clip in background (non-blocking)
      const warmKey = modelURLRef.current || vrmPath || "";
      if (warmKey && idlePathRef.current) {
        runOnIdle(() => {
          void vrmCache
            .createClipIfMissing(warmKey, idlePathRef.current, loader, currentVrm.current)
            .catch((e) => console.error("warm clip failed:", e));
        });
      }
    };

    loadModel()
      .catch((error) => {
        console.error("Error loading model:", error);
      })
      .finally(() => {
        const currentPathRequest = inFlightLoadByPathRef.current.get(vrmPath);
        if (currentPathRequest === requestId) {
          inFlightLoadByPathRef.current.delete(vrmPath);
        }
      });
  }, [vrmPath]);

  // アニメーションのファイル拡張子を判定するヘルパー関数
  const getFileExtension = (path: string, extension?: string): string => {
    if (extension) return extension;
    // Blob URLの場合は末尾の拡張子を取得
    return path.split(".").pop() || "";
  };

  // ファイル拡張子が指定された拡張子と一致するかチェック(ドット有無両対応)
  const isExtension = (fileExt: string, targetExt: string): boolean => {
    const normalized = fileExt.startsWith(".") ? fileExt.substring(1) : fileExt;
    return normalized === targetExt;
  };

  // アニメーションを読み込んで再生する関数
  // eslint-disable-next-line complexity
  const loadAnimation = (path: string, extension?: string) => {
    if (!path || isAnimationLoading.current || !currentVrm.current?.scene) return;

    // 拡張子を判定(引数で渡された場合はそれを使用、なければURLから判定)
    const fileExtension = getFileExtension(path, extension);
    if (!fileExtension) {
      return;
    }

    isAnimationLoading.current = true;
    // ここで現在のアニメーションパスを記録
    animationPathRef.current = path;

    const vrmKey = modelURLRef.current || vrmPath || "";

    const inlineCreateFromVrma = (loadPath: string) => {
      loader.load(
        loadPath,
        (gltf) => {
          const vrmAnimations = gltf.userData.vrmAnimations;
          if (vrmAnimations === null) {
            console.error("[loadAnimation] vrmAnimations is null");
            isAnimationLoading.current = false;
            return;
          }
          currentVrmAnimation.current = vrmAnimations[0] ?? null;
          // create clip in next frame
          requestAnimationFrame(() => {
            try {
              const clip = createVRMAnimationClip(currentVrmAnimation.current, currentVrm.current);
              initAnimationClip(clip);
            } catch (e) {
              console.error("[loadAnimation] createVRMAnimationClip failed:", e);
              isAnimationLoading.current = false;
            }
          });
        },
        undefined,
        (error) => {
          console.error("[loadAnimation] Error loading .vrma:", error);
          isAnimationLoading.current = false;
        }
      );
    };

    if (isExtension(fileExtension, "vrma")) {
      // try cache first
      if (vrmKey) {
        const cached = vrmCache.getClip(vrmKey, path);
        if (cached) {
          runOnIdle(() => {
            try {
              initAnimationClip(cached);
            } catch (e) {
              console.error("[loadAnimation] initAnimationClip failed for cached clip:", e);
              isAnimationLoading.current = false;
            }
          });
          return;
        }
      }

      // schedule warm/create on idle
      if (vrmKey) {
        runOnIdle(() => {
          void vrmCache
            .createClipIfMissing(vrmKey, path, loader, currentVrm.current)
            .then((clip) => {
              if (clip) {
                try {
                  initAnimationClip(clip);
                } catch (e) {
                  console.error("[loadAnimation] initAnimationClip failed:", e);
                  isAnimationLoading.current = false;
                }
              } else {
                inlineCreateFromVrma(path.slice(0, -5));
              }
            })
            .catch((err) => {
              console.error("[loadAnimation] createClipIfMissing failed:", err);
              isAnimationLoading.current = false;
            });
        });
      } else {
        // no VRM key available, fallback to inline
        inlineCreateFromVrma(path.slice(0, -5));
      }

      return;
    }

    if (isExtension(fileExtension, "fbx")) {
      const loadPath = path.slice(0, -4);
      currentMixer.current = new THREE.AnimationMixer(currentVrm.current.scene);

      if (vrmKey) {
        const cached = vrmCache.getClip(vrmKey, path);
        if (cached) {
          runOnIdle(() => {
            try {
              currentVrmAnimation.current = cached;
              initAnimationClip(cached);
            } catch (e) {
              console.error("[loadAnimation] initAnimationClip failed for cached FBX:", e);
              isAnimationLoading.current = false;
            }
          });
          return;
        }
      }

      // Warm/create on idle then init; fallback to direct load
      if (vrmKey) {
        runOnIdle(() => {
          void vrmCache
            .createClipIfMissing(vrmKey, path, loader, currentVrm.current)
            .then((clip) => {
              if (clip) {
                currentVrmAnimation.current = clip;
                requestAnimationFrame(() => {
                  try {
                    initAnimationClip(clip);
                  } catch (e) {
                    console.error("[loadAnimation] initAnimationClip failed:", e);
                    isAnimationLoading.current = false;
                  }
                });
              } else {
                loadMixamoAnimation(loadPath, currentVrm.current)
                  .then((clip) => {
                    currentVrmAnimation.current = clip;
                    requestAnimationFrame(() => {
                      try {
                        initAnimationClip(clip);
                      } catch (e) {
                        console.error("[loadAnimation] initAnimationClip failed:", e);
                        isAnimationLoading.current = false;
                      }
                    });
                  })
                  .catch((error) => {
                    console.error("[loadAnimation] Error loading .fbx:", error);
                    isAnimationLoading.current = false;
                  });
              }
            })
            .catch((err) => {
              console.error("[loadAnimation] createClipIfMissing failed:", err);
              isAnimationLoading.current = false;
            });
        });
      } else {
        loadMixamoAnimation(loadPath, currentVrm.current)
          .then((clip) => {
            currentVrmAnimation.current = clip;
            requestAnimationFrame(() => {
              try {
                initAnimationClip(clip);
              } catch (e) {
                console.error("[loadAnimation] initAnimationClip failed:", e);
                isAnimationLoading.current = false;
              }
            });
          })
          .catch((error) => {
            console.error("[loadAnimation] Error loading .fbx:", error);
            isAnimationLoading.current = false;
          });
      }

      return;
    }

    console.error(`Unknown file extension: ${fileExtension}`);
    isAnimationLoading.current = false;
  };

  const moveModel = (targetDelta: THREE.Vector3, time: number) => {
    if (modelRef.current) {
      // 移動先の位置
      const originPosition = modelRef.current.position.toArray();
      // 元の座標からtargetDeltaだけ変化
      const targetPosition = originPosition.map((v, i) => v + targetDelta.toArray()[i]);
      // Tween.jsを使用して位置を滑らかに変更
      const tween = new TWEEN.Tween(originPosition)
        .to(targetPosition, time) // 2秒かけて移動
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onUpdate((newPosition) => {
          if (modelRef.current) {
            modelRef.current.position.set(...newPosition);
          }
        })
        .start();
      let animationId: number;

      // アニメーションを更新するための関数
      const animate = (time: number) => {
        if (TWEEN.update(time)) {
          // アニメーションがまだ終了していない場合は次のフレームをリクエスト
          animationId = requestAnimationFrame(animate);
        }
      };

      // アニメーションを開始
      animationId = requestAnimationFrame(animate);
      // アニメーションが終了したらrequestAnimationFrameを止める
      tween.onComplete(() => {
        cancelAnimationFrame(animationId);
      });
    }
  };
  const rotateModel = (targetDelta: number[], time: number) => {
    if (modelRef.current) {
      // 現在の回転を配列に変換
      const originRotation = modelRef.current.rotation.toArray() as number[];

      // targetDelta を度数からラジアンに変換
      const targetDeltaRadians = targetDelta.map((deg) => THREE.MathUtils.degToRad(deg));

      // 目標の回転を計算
      const targetRotation = originRotation.map((v, i) => v + targetDeltaRadians[i]);

      // Tween.jsを使用して回転をアニメーション
      const tween = new TWEEN.Tween(originRotation)
        .to(targetRotation, time) // time ミリ秒かけて回転
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onUpdate((newRotation) => {
          if (modelRef.current) {
            modelRef.current.rotation.set(newRotation[0], newRotation[1], newRotation[2]);
          }
        })
        .start();

      let animationId: number;

      // アニメーションを更新するための関数
      const animate = (time: number) => {
        if (TWEEN.update(time)) {
          // アニメーションがまだ終了していない場合は次のフレームをリクエスト
          animationId = requestAnimationFrame(animate);
        }
      };

      // アニメーションを開始
      animationId = requestAnimationFrame(animate);

      // アニメーションが終了したらrequestAnimationFrameを止める
      tween.onComplete(() => {
        cancelAnimationFrame(animationId);
      });
    }
  };
  useEffect(() => {
    if (position[0] === 0 && position[1] === 0 && position[2] === 0 && position[3] === 0) return;
    moveModel(new THREE.Vector3(position[0], position[1], position[2]), position[3]);
    setPosition([0, 0, 0, 0]);
  }, [position]);
  useEffect(() => {
    if (rotation[0] === 0 && rotation[1] === 0 && rotation[2] === 0 && position[3] === 0) return;
    rotateModel([rotation[0], rotation[1], rotation[2]], position[3]);
    setRotation([0, 0, 0, 0]);
  }, [rotation]);

  // 3Dモデルを初期固定読み込み（characterIndexが-1の場合のみ、初回起動時にcharacters[0]をロード）
  useEffect(() => {
    if (isInitLoaded && isCharacterLoaded) {
      const initialVrm = vrmList.find((vrm) => /\.vrm$/i.test(vrm.name));
      if (!initialVrm) return;

      if (vrmPath === "") {
        // プリロード時にmodelURLRefが設定済みのため、リセットして強制ロードを有効にする
        setCharacterIndex(0);
        modelURLRef.current = "";
        setVRMPath(initialVrm.url);
      }
      setIsInitLoaded(false);
      // 1秒後に切り替え
      setTimeout(() => {
        setIsAllLoaded(true);
      }, 1000);
    }
  }, [isInitLoaded, isCharacterLoaded, vrmList]);

  return (
    <>
      <Html center zIndexRange={[900, 0]}>
        <div className={isCharacterLoaded ? "foreground progress-fade-out" : "foreground"}>
          <div
            className={isCharacterLoaded ? "model-progress progress-fade-out" : "model-progress"}
          >
            {progress} % <br />
            loaded
          </div>
        </div>
      </Html>
      {gltf ? (
        <primitive object={gltf.scene} ref={modelRef} scale={1} position={defaultPosition} />
      ) : (
        <></>
      )}
    </>
  );
};

export default Model;
